import * as React from 'react'
import styled from 'styled-components'
import { FormikHelpers } from 'formik'

import {
  Email,
  Form,
  Forms,
  PhoneNumber,
  SecurityQuestionGroup,
  NewUsername,
} from '@ally/forms'
import {
  Box,
  Button,
  Space,
  TextBody,
  TextHeading,
  upToMd,
} from '@ally/metronome-ui'
import { BadWords } from '@ally/use-validation'
import {
  EnrollCredentialCreationProps,
  EnrollCredentialCreationPayload,
  AllyUserRole,
} from '@ally/transmitigator'
import { LoginHeading } from '@ally/ally-login'

import { CIAMErrorBanner, InputWrapper } from '../Shared'
import { CreatePassword } from '.'
import { PasswordHint } from './PasswordHint'
import { useTransmit } from '../../TransmitContext'
import { useAppState } from '../../AppStateProvider'

const StyledCopy = styled(TextBody)`
  ${({ theme: { space } }): string => `
    &:last-of-type {
      margin-bottom: ${space.md};
    }
  `}
`

const DisclaimerCopy = styled(TextBody)`
  ${({ theme: { space } }): string => `
    margin-bottom: ${space.md};
    max-width: 600px;
  `}
`

const StyledTextHeading = styled(TextHeading)`
  ${({ theme: { space } }): string => `
    margin-bottom: ${space.xs} !important;
  `}
`

const ContactInfoForm: React.FC = () => {
  const { isWebWrapped } = useAppState()
  const alertPrefText = isWebWrapped ? 'Profile' : 'Profile and Settings'

  return (
    <>
      <Space mb="md">
        <StyledTextHeading tag="h2" size="sm">
          Contact Information
        </StyledTextHeading>

        <StyledCopy tag="p" size="sm">
          We&apos;ll go ahead and add this info to your profile, and use it
          anytime we need to get in touch.
        </StyledCopy>
      </Space>
      <InputWrapper mb="md">
        <Email
          data-testid="credential-creation-email"
          name="email"
          data-allytmfn="email"
          required
        />
      </InputWrapper>
      <InputWrapper mb="md">
        <PhoneNumber
          data-testid="credential-creation-phone"
          name="phone"
          data-allytmfn="phone"
          required
        />
      </InputWrapper>

      <Space mb="md">
        <DisclaimerCopy tag="p" size="sm">
          You authorize us to contact you by using any telephone number you
          provide to us, including a mobile or cell phone number that you are
          authorized to use. In addition to manual calling, we may use text
          messages, prerecorded or artificial voice messages, or automatic
          dialing systems. We will not charge you for any contact, but your
          phone service provider may.
        </DisclaimerCopy>

        <DisclaimerCopy tag="p" size="sm">
          You can manage your alert preferences in your{' '}
          <TextBody size="sm" tag="b" weight="bold">
            {alertPrefText}
          </TextBody>
          .
        </DisclaimerCopy>
      </Space>
    </>
  )
}

interface CredentialCreationFormInputProps {
  username: string
  password: string
  passwordHint: string
  credentialCreationSecurityQuestion: string
  credentialCreationSecurityAnswer: string
  phone?: string
  email?: string
  smsConsent: boolean
  voiceConsent: boolean
  allyUserRoles?: AllyUserRole
}

const StyledSecurityQuestionGroup = styled(SecurityQuestionGroup)`
  ${({ theme: { media } }): string => `
    ${media.up('lg')} {
      p {
        min-width: 600px;
      }
    }
  `};
`

export const CredentialCreationContent: React.FC<EnrollCredentialCreationProps> = ({
  onComplete,
  status,
  email,
  phone,
  error,
  suggestedUsername1,
  suggestedUsername2,
  allyUserRoles = {} as AllyUserRole,
}) => {
  const { hostServices } = useTransmit()

  const formId = 'customer-credential-creation-form'
  const initialValues: CredentialCreationFormInputProps = {
    username: '',
    password: '',
    passwordHint: '',
    credentialCreationSecurityQuestion: '',
    credentialCreationSecurityAnswer: '',
    phone,
    email,
    smsConsent: true, // Checkbox removed (CIAM-12673) but backend still requires this prop
    voiceConsent: true, // Checkbox removed (CIAM-12673) but backend still requires this prop
  }

  const badWords = hostServices?.hostData.badWords.data
  const badWordsObject: BadWords = badWords || {
    base: [],
    names: [],
    substring: [],
  }

  const handleSubmit = (
    {
      username,
      password,
      passwordHint,
      credentialCreationSecurityQuestion,
      credentialCreationSecurityAnswer,
      email: emailValue,
      phone: phoneValue,
      smsConsent,
      voiceConsent,
    }: CredentialCreationFormInputProps,
    { setSubmitting }: FormikHelpers<CredentialCreationFormInputProps>,
  ): void => {
    const formValues: EnrollCredentialCreationPayload = {
      username,
      password,
      passwordHint,
      secretQuestion: credentialCreationSecurityQuestion,
      secretAnswer: credentialCreationSecurityAnswer,
      smsConsent,
      voiceConsent,
    }

    formValues.email = emailValue
    formValues.phone = phoneValue

    onComplete(formValues)

    // Required to resubmit upon failure since we are not returning a promise here
    setSubmitting(false)
  }

  const SecurityQuestionWrapper: React.FC = () => {
    return (
      <InputWrapper mb="md">
        <StyledSecurityQuestionGroup
          answerName="credentialCreationSecurityAnswer"
          badWordsObject={badWordsObject}
          questionName="credentialCreationSecurityQuestion"
          required
        />
      </InputWrapper>
    )
  }

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Enroll:Create User Details',
    })
  }, [])

  return (
    <Box width={upToMd(12, 6)}>
      <LoginHeading title="Your profile" />

      <StyledCopy tag="p" size="sm">
        Make your login info easy for you to remember, but hard for anyone else
        to guess.
      </StyledCopy>

      <CIAMErrorBanner error={error} />

      <Forms
        formId={formId}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <Form id={formId}>
          <InputWrapper mb="md">
            <NewUsername
              label="Username"
              name="username"
              badWords={badWordsObject}
            />
          </InputWrapper>

          <CreatePassword
            title="Password"
            fieldName="password"
            dataTestId="credential-creation-create-password"
          />

          <PasswordHint
            badWords={badWordsObject}
            title="Password Hint"
            dataTestId="credential-creation-password-hint"
          />

          {allyUserRoles.bank && <SecurityQuestionWrapper />}

          <ContactInfoForm />

          <Button
            allytmln="continue"
            text="Continue"
            type="submit"
            variant="primary"
            loading={status === 'Loading'}
            fullWidthMd
          />
        </Form>
      </Forms>
    </Box>
  )
}

export const CredentialCreation: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'EnrollCredentialCreation') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <CredentialCreationContent {...activeScreen.payload} />
}
